/**
 * **"正在连接"这件事的状态机。** 纯函数，不碰网络、不碰计时器。
 *
 * 评审：「在修复网络问题之前，即便连不上网也能创建房间，这会误导玩家。应当加一个
 * "正在连接"页面，无论是正在加入游戏还是正在创建房间都进入这个界面，
 * 若出现网络问题就返回之前的页面。」
 *
 * ## 为什么需要一个页面、而不只是等一下
 *
 * 改之前 `createRoom` / `joinRoom` 是**同步**的：点下去就生出本机的大厅状态、
 * 直接进房间页。于是"房间建好了"和"网络通了"是两件事，而界面上只有前者 ——
 * 网线拔了、中继全挂了、房间号根本不存在，看到的都是同一个"房间已开好"。
 * 那种界面比报错更糟：它让玩家在错误的认知上继续操作。
 *
 * 现在的顺序是：**先证明网络通，再进房间**。证明分两步：
 *
 * | | 建房间 | 加入 |
 * | --- | --- | --- |
 * | ① 信令中继连得上吗 | 要 | 要 |
 * | ② 房间里有人应答吗 | 不需要 | **要**（没有就是房间号不对/对方不在） |
 *
 * 这两步各自对应一个相位，任何一步过不去都落到 `failed` —— 界面把那一句
 * 理由显示出来，然后**自己退回上一页**。
 *
 * ## 相位
 *
 * ```
 *   idle ──start──▶ probing ──reachable──┬─ create ─▶ ready
 *                    │                   └─ join ───▶ searching ──peer──▶ ready
 *                    │                                   │
 *                    └──────── unreachable ──────────────┴──▶ failed
 *   （cancel 从任何一步回到 idle）
 * ```
 *
 * ⚠️ `probing` 和 `searching` 分开，是为了让界面能说清"卡在哪一步"：
 * 连不上中继是**网络问题**，找不到房间是**房间号/对方的问题**。
 * 合成一个 `connecting` 的话，玩家只能看到一个转不完的圈。
 */

import { t } from '../i18n';

/** 连接走到哪一步了 */
export type ConnectPhase = 'idle' | 'probing' | 'searching' | 'ready' | 'failed';

/** 点的是哪一个按钮。它决定第二步要不要等对端 */
export type ConnectMode = 'create' | 'join';

export interface ConnectState {
  readonly phase: ConnectPhase;
  readonly mode: ConnectMode;
  /** 正在连的房间号。空 = 没有在连 */
  readonly code: string;
  /** `failed` 时的那句话。其它相位是 `null` */
  readonly reason: string | null;
}

export const IDLE_CONNECT: ConnectState = Object.freeze({
  phase: 'idle',
  mode: 'create',
  code: '',
  reason: null,
});

export type ConnectEvent =
  /** 点下了"创建房间"或"确认加入" */
  | { readonly t: 'start'; readonly mode: ConnectMode; readonly code: string }
  /** 信令探测的结果 */
  | { readonly t: 'reachable' }
  | { readonly t: 'unreachable'; readonly reason: string }
  /** 看到房间里有人了 */
  | { readonly t: 'peer'; readonly count: number }
  /** 失败（超时、被拒……）。理由直接给玩家看 */
  | { readonly t: 'fail'; readonly reason: string }
  | { readonly t: 'cancel' };

/**
 * 走一步。**纯函数** —— 于是"什么时候该退回上一页"这件事能在 node 里断言，
 * 而不是靠在真网络上反复试。
 *
 * ⚠️ `peer` 只在 `probing` / `searching` 里有效。已经 `ready` 之后再收到
 * 一条"有人来了"不该改变什么（那是大厅该管的事）；已经 `failed` 之后同理。
 */
export function reduceConnect(state: ConnectState, event: ConnectEvent): ConnectState {
  switch (event.t) {
    case 'cancel':
      return IDLE_CONNECT;
    case 'start':
      return { phase: 'probing', mode: event.mode, code: event.code, reason: null };
    case 'unreachable':
      // 同 `fail`：`idle`（还没开始）和 `ready`（已经连上）都不该被改写
      if (state.phase === 'idle' || state.phase === 'ready') return state;
      return { ...state, phase: 'failed', reason: event.reason };
    case 'fail':
      // ⚠️ 已经连上了就别再被一条**迟到**的失败改写了：那个"等超时"的计时器
      //    可能比"找到对端"晚落地，晚一步就会把 ready 写成 failed
      if (state.phase === 'idle' || state.phase === 'ready') return state;
      return { ...state, phase: 'failed', reason: event.reason };
    case 'reachable':
      if (state.phase !== 'probing') return state;
      // 建房间不需要等任何人 —— 中继通了，这个房间就已经在信令网络上了
      return { ...state, phase: state.mode === 'create' ? 'ready' : 'searching' };
    case 'peer':
      if (state.phase !== 'searching' && state.phase !== 'probing') return state;
      return event.count > 0 ? { ...state, phase: 'ready' } : state;
  }
}

/** 这个相位是不是"还在忙"。界面据此决定要不要继续显示转圈/禁用返回 */
export function isConnecting(phase: ConnectPhase): boolean {
  return phase === 'probing' || phase === 'searching';
}

/**
 * **没网就别试了。**
 *
 * `navigator.onLine` 的语义很弱（它只知道"有没有网络接口"，不知道能不能到
 * 外网），所以它**只用来短路失败** —— 说 false 的时候基本可以确信连不上，
 * 说 true 的时候什么都证明不了（还是得真的探一次中继）。
 *
 * 但它值得留着：没网的时候等五秒超时，和立刻告诉玩家"这台设备没网"，
 * 是完全不同的体验。
 */
export function offlineReason(online: boolean): string | null {
  return online ? null : t('connect.offline');
}
